import { ChevronDown, Save, Settings } from "lucide-react";
import { useState } from "react";
import type { RagCollection } from "../../services/rag";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";

interface CollectionSettingsProps {
	collection: RagCollection;
	onUpdate: (settings: any) => void;
}

export function CollectionSettings({
	collection,
	onUpdate,
}: CollectionSettingsProps) {
	const [open, setOpen] = useState(false);
	const [chunkSize, setChunkSize] = useState(collection.chunkSize);
	const [chunkOverlap, setChunkOverlap] = useState(collection.chunkOverlap);
	const [topK, setTopK] = useState(collection.topK);
	const [similarityThreshold, setSimilarityThreshold] = useState(
		collection.similarityThreshold,
	);

	const hasChanges =
		chunkSize !== collection.chunkSize ||
		chunkOverlap !== collection.chunkOverlap ||
		topK !== collection.topK ||
		similarityThreshold !== collection.similarityThreshold;

	const handleSave = () => {
		onUpdate({
			chunkSize,
			chunkOverlap,
			topK,
			similarityThreshold,
		});
	};

	const handleReset = () => {
		setChunkSize(collection.chunkSize);
		setChunkOverlap(collection.chunkOverlap);
		setTopK(collection.topK);
		setSimilarityThreshold(collection.similarityThreshold);
	};

	return (
		<Card>
			<CardHeader
				className="flex flex-row items-center justify-between space-y-0 pb-0 cursor-pointer hover:bg-muted/50 transition-colors rounded-t-lg"
				onClick={() => setOpen(!open)}
			>
				<div className="flex items-center gap-2">
					<Settings className="h-5 w-5" />
					<CardTitle>Collection Settings</CardTitle>
				</div>
				<ChevronDown
					className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
				/>
			</CardHeader>
			{open && (
				<CardContent className="space-y-6 pt-4">
					<div className="space-y-2">
						<Label htmlFor="chunkSize">Chunk Size (tokens)</Label>
						<Input
							id="chunkSize"
							type="number"
							min={100}
							max={2048}
							value={chunkSize}
							onChange={(e) => setChunkSize(Number(e.target.value))}
						/>
						<p className="text-xs text-muted-foreground">
							Number of tokens per chunk (100-2048)
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="chunkOverlap">Chunk Overlap (tokens)</Label>
						<Input
							id="chunkOverlap"
							type="number"
							min={0}
							max={200}
							value={chunkOverlap}
							onChange={(e) => setChunkOverlap(Number(e.target.value))}
						/>
						<p className="text-xs text-muted-foreground">
							Overlap between chunks to maintain context (0-200, must be less
							than chunk size)
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="topK">Top-K Results</Label>
						<Input
							id="topK"
							type="number"
							min={1}
							max={20}
							value={topK}
							onChange={(e) => setTopK(Number(e.target.value))}
						/>
						<p className="text-xs text-muted-foreground">
							Number of most relevant chunks to retrieve (1-20)
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="similarityThreshold">
							Similarity Threshold: {similarityThreshold.toFixed(2)}
						</Label>
						<Slider
							id="similarityThreshold"
							min={0.5}
							max={0.95}
							step={0.05}
							value={[similarityThreshold]}
							onValueChange={(value) => setSimilarityThreshold(value[0])}
						/>
						<p className="text-xs text-muted-foreground">
							Minimum similarity score for chunk retrieval (0.5-0.95)
						</p>
					</div>

					<div className="flex items-center justify-end gap-2">
						{hasChanges && (
							<Button variant="outline" onClick={handleReset}>
								Reset
							</Button>
						)}
						<Button onClick={handleSave} disabled={!hasChanges}>
							<Save className="mr-2 h-4 w-4" />
							Save Settings
						</Button>
					</div>
				</CardContent>
			)}
		</Card>
	);
}
